// Catalog.ID Client — XRPL Master Key Sheet
// ============================================================================
// New in round 5 (2026-09-03): the identity's XRPL master signing key on
// paper, so ALL of the account's sensitive material is bundled in the one
// printed kit. The keypair is generated at registration together with the
// other account keys and lives in the key bag as `xrpl_master`
// (crypto/xrpl.ts); this sheet is its only printed form.
//
// Both values are typeable, so per the booklet's design rules there is NO QR
// (decided 2026-09-03). The Address is fully derivable from the Secret — it is
// printed anyway so the account can be looked up, and a re-derivation checked,
// without trusting any tooling. Nothing else (public key etc.) earns its ink:
// it is all derivable from the Secret.

import type { Content } from 'pdfmake/interfaces';
import { accountHeader, type AccountHeaderData } from './shared';

export interface XrplKeyPageData {
    header: AccountHeaderData;
    /** Classic address ("r..."), derived from the secret. */
    address: string;
    /** Family seed ("sEd..."), the key itself. */
    secret: string;
}

export function buildXrplKeyPage(data: XrplKeyPageData): Content[] {
    return [
        { text: 'XRPL Master Key', style: 'pageTitle' },
        accountHeader(data.header),

        {
            text: 'This is the master signing key for XRPL (XRP Ledger) accounts belonging to this '
                + 'identity. It was generated on your own device during registration, alongside the '
                + 'account\'s other keys, and is kept encrypted on the account with them.',
            style: 'value',
            margin: [0, 0, 0, 14],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Address', style: 'section' },
                { text: data.address, style: 'credential', margin: [0, 2, 0, 2] },
                {
                    text: 'The key\'s XRPL account id. It is derived from the secret below and is '
                        + 'printed for looking the account up and for verification.',
                    style: 'note',
                },
            ],
            margin: [0, 0, 0, 14],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Secret', style: 'section' },
                { text: data.secret, style: 'credential', margin: [0, 2, 0, 2] },
                {
                    text: 'The key itself, as an XRPL family seed. Everything else — the public key '
                        + 'and the address above — can be derived from it.',
                    style: 'note',
                },
            ],
            margin: [0, 0, 0, 14],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'How it is used', style: 'section' },
                {
                    text: 'This key is used only for signing. When an XRPL wallet is created for this '
                        + 'identity later, that wallet is configured to accept this key as its master '
                        + 'signing account — so one printed key can authorize transactions for every '
                        + 'wallet the identity creates. The key\'s own account needs no funding and no '
                        + 'presence on the ledger for this to work.',
                    style: 'value',
                    margin: [0, 0, 0, 6],
                },
                {
                    text: 'If you never create an XRPL wallet, this key is dormant: it signs nothing, '
                        + 'holds nothing and does nothing.',
                    style: 'value',
                },
            ],
            margin: [0, 0, 0, 14],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Keep This Sheet Safe', style: 'section' },
                {
                    ul: [
                        'Anyone holding the secret can sign for every wallet that trusts this key.',
                        'Keep it in a secure location; do not photograph it or store scans of it.',
                        'An account recovery hands this key over together with the rest of the '
                            + 'identity\'s keys.',
                    ],
                    style: 'note',
                },
            ],
        },
    ];
}
